import {
  GoodsExtraField,
  MrhstConfig,
  MrhstConfigKey,
  MrhstGoodsColumn,
} from './mrhst.types';

/**
 * 공통 상품 추가 필드 - costToTeam
 */
const COST_TO_TEAM_FIELD: GoodsExtraField = {
  name: 'costToTeam',
  label: 'REPO.TABLE.goods.costToTeam',
  type: 'number',
  colClass: 'col-md-3',
};

/**
 * 공통 상품 컬럼 정의
 */
const COMMON_GOODS_COLUMNS: MrhstGoodsColumn[] = [
  {
    key: 'id',
    name: 'REPO.TABLE.goods.id',
    type: 'number',
    width: '80px',
    class: 'text-center',
    sortable: true,
  },
  {
    key: 'goodsNm',
    name: 'REPO.TABLE.goods.goodsNm',
    type: 'text',
    sortable: true,
  },
  {
    key: 'activeFl',
    name: 'REPO.TABLE.goods.activeFl',
    type: 'boolean',
    width: '80px',
    class: 'text-center',
  },
  {
    key: 'goodsAmt',
    name: 'REPO.TABLE.goods.goodsAmt',
    type: 'number',
    width: '120px',
    class: 'text-end',
    sortable: true,
  },
];

/**
 * Mrhst Configurations (Fully Config-Driven)
 *
 * Board 패턴을 따라 7개 시설 유형 + ETC 서브타입을 단일 컴포넌트로 통합
 *
 * Legacy 투명 마이그레이션:
 * - URL: /master/{type}-mrhst (Legacy와 동일)
 * - 데이터: IMrhst (기존 모델 그대로 사용)
 */
export const MRHST_CONFIGS: Record<MrhstConfigKey, MrhstConfig> = {
  hotel: {
    title: 'brandFn.menu.hotel_mrhst',
    mrhstType: 'HOTEL',
    label: '숙박시설',
    goodsLabel: '객실',
    goodsApiPath: 'api/hotelGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'tel',
        name: 'REPO.TABLE.mrhst.tel',
        type: 'text',
        width: '150px',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: [
      ...COMMON_GOODS_COLUMNS,
      {
        key: 'capacity',
        name: 'REPO.TABLE.goods.capacity',
        type: 'number',
        width: '100px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.goods.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    hasOptions: true,
    goodsOptFieldName: 'hotelGoodsOptList',
    goodsOptMasterFieldName: 'hotelGoodsOptMasterList',
    hasPriceCalendar: true,
    excelTemplate: 'hotel-mrhst',
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/hotel-mrhst',
    permissions: ['HOTEL_GOODS$READ'],
    goodsExtraFields: [
      {
        name: 'roomType',
        label: 'REPO.TABLE.goods.roomType',
        type: 'select',
        colClass: 'col-md-3',
        options: [
          { value: 'SGL', label: 'SGL' },
          { value: 'TWN', label: 'TWN' },
          { value: 'TRP', label: 'TRP' },
          { value: 'QUD', label: 'QUD' },
          { value: 'ETC', label: 'ETC' },
        ],
      },
      {
        name: 'sellCapacity',
        label: 'REPO.TABLE.goods.sellCapacity',
        type: 'number',
        colClass: 'col-md-3',
      },
    ],
    hmpgPath: 'mrhst',
    tabs: [
      {
        key: 'basicInfo',
        label: 'mrhst.tab.basicInfo',
        icon: 'bi-info-circle',
      },
      { key: 'media', label: 'mrhst.tab.media', icon: 'bi-images' },
      {
        key: 'introContent',
        label: 'mrhst.tab.introContent',
        icon: 'bi-text-paragraph',
      },
      {
        key: 'roomManagement',
        label: 'mrhst.tab.roomManagement',
        icon: 'bi-door-open',
      },
      {
        key: 'locationContact',
        label: 'mrhst.tab.locationContact',
        icon: 'bi-geo-alt',
      },
      {
        key: 'noticePolicy',
        label: 'mrhst.tab.noticePolicy',
        icon: 'bi-clipboard-check',
      },
      {
        key: 'bookingSettings',
        label: 'mrhst.tab.bookingSettings',
        icon: 'bi-calendar-check',
      },
      { key: 'display', label: 'mrhst.tab.display', icon: 'bi-eye' },
      { key: 'layout', label: 'mrhst.tab.layout', icon: 'bi-layout-wtf' },
    ],
  },
  car: {
    title: 'brandFn.menu.car_mrhst',
    mrhstType: 'CAR',
    label: '렌터카',
    goodsLabel: '차량',
    goodsApiPath: 'api/carGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'tel',
        name: 'REPO.TABLE.mrhst.tel',
        type: 'text',
        width: '150px',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: COMMON_GOODS_COLUMNS,
    hasOptions: true,
    goodsOptFieldName: 'carGoodsOptList',
    goodsOptMasterFieldName: 'carGoodsOptMasterList',
    excelTemplate: 'car-mrhst',
    goodsExtraFields: [
      {
        name: 'carGoodsType',
        label: 'REPO.TABLE.goods.carGoodsType',
        type: 'select',
        colClass: 'col-md-3',
        options: [
          { value: 'SMALL', label: 'REPO.TABLE.goods.carGoodsType.SMALL' },
          { value: 'COMPACT', label: 'REPO.TABLE.goods.carGoodsType.COMPACT' },
          { value: 'MIDSIZE', label: 'REPO.TABLE.goods.carGoodsType.MIDSIZE' },
          { value: 'LARGE', label: 'REPO.TABLE.goods.carGoodsType.LARGE' },
          { value: 'WAGON', label: 'REPO.TABLE.goods.carGoodsType.WAGON' },
          { value: 'MINIVAN', label: 'REPO.TABLE.goods.carGoodsType.MINIVAN' },
          { value: 'ECO', label: 'REPO.TABLE.goods.carGoodsType.ECO' },
          { value: 'SUV', label: 'REPO.TABLE.goods.carGoodsType.SUV' },
          { value: 'LUXURY', label: 'REPO.TABLE.goods.carGoodsType.LUXURY' },
        ],
      },
      {
        name: 'pickupType',
        label: 'REPO.TABLE.goods.pickupType',
        type: 'select',
        colClass: 'col-md-3',
        options: [
          { value: 'MEETING', label: 'REPO.TABLE.goods.pickupType.MEETING' },
          { value: 'SENDING', label: 'REPO.TABLE.goods.pickupType.SENDING' },
          { value: 'STAY', label: 'REPO.TABLE.goods.pickupType.STAY' },
          { value: 'RENT', label: 'REPO.TABLE.goods.pickupType.RENT' },
        ],
      },
      COST_TO_TEAM_FIELD,
    ],
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/car-mrhst',
    permissions: ['CAR_GOODS$READ'],
  },
  attraction: {
    title: 'brandFn.menu.attraction_mrhst',
    mrhstType: 'ATTRACTION',
    label: '관광지',
    goodsLabel: '상품',
    goodsApiPath: 'api/attractionGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'tel',
        name: 'REPO.TABLE.mrhst.tel',
        type: 'text',
        width: '150px',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: COMMON_GOODS_COLUMNS,
    hasOptions: true,
    goodsOptFieldName: 'attractionGoodsOptList',
    goodsOptMasterFieldName: 'attractionGoodsOptMasterList',
    excelTemplate: 'attraction-mrhst',
    goodsExtraFields: [
      {
        name: 'pickupType',
        label: 'REPO.TABLE.goods.pickupType',
        type: 'select',
        colClass: 'col-md-3',
        options: [
          { value: 'MEETING', label: 'REPO.TABLE.goods.pickupType.MEETING' },
          { value: 'SENDING', label: 'REPO.TABLE.goods.pickupType.SENDING' },
          { value: 'STAY', label: 'REPO.TABLE.goods.pickupType.STAY' },
        ],
      },
      COST_TO_TEAM_FIELD,
    ],
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/attraction-mrhst',
    permissions: ['ATTRACTION_GOODS$READ'],
  },
  restaurant: {
    title: 'brandFn.menu.restaurant_mrhst',
    mrhstType: 'RESTAURANT',
    label: '식당',
    goodsLabel: '메뉴',
    goodsApiPath: 'api/restaurantGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'tel',
        name: 'REPO.TABLE.mrhst.tel',
        type: 'text',
        width: '150px',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: COMMON_GOODS_COLUMNS,
    hasOptions: true,
    goodsOptFieldName: 'restaurantGoodsOptList',
    goodsOptMasterFieldName: 'restaurantGoodsOptMasterList',
    excelTemplate: 'restaurant-mrhst',
    goodsExtraFields: [COST_TO_TEAM_FIELD],
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/restaurant-mrhst',
    permissions: ['RESTAURANT_GOODS$READ'],
  },
  guide: {
    title: 'brandFn.menu.guide_mrhst',
    mrhstType: 'GUIDE',
    label: '가이드',
    goodsLabel: '상품',
    goodsApiPath: 'api/guideGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'tel',
        name: 'REPO.TABLE.mrhst.tel',
        type: 'text',
        width: '150px',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: COMMON_GOODS_COLUMNS,
    hasOptions: true,
    goodsOptFieldName: 'guideGoodsOptList',
    goodsOptMasterFieldName: 'guideGoodsOptMasterList',
    excelTemplate: 'guide-mrhst',
    goodsExtraFields: [COST_TO_TEAM_FIELD],
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/guide-mrhst',
    permissions: ['GUIDE_GOODS$READ'],
  },
  farm: {
    title: 'brandFn.menu.farm_mrhst',
    mrhstType: 'FARM',
    label: '농원',
    goodsLabel: '상품',
    goodsApiPath: 'api/farmGoods',
    viewType: 'table',
    searchFields: [
      { name: 'mrhstNm', type: 'text', label: 'REPO.TABLE.mrhst.mrhstNm' },
      {
        name: 'openFl',
        type: 'select',
        label: 'REPO.TABLE.mrhst.openFl',
        options: [
          { value: 'true', label: 'REPO.TABLE.mrhst.openFl.open' },
          { value: 'false', label: 'REPO.TABLE.mrhst.openFl.closed' },
        ],
      },
    ],
    columns: [
      {
        key: 'id',
        name: 'REPO.TABLE.mrhst.id',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
      {
        key: 'mrhstNm',
        name: 'REPO.TABLE.mrhst.mrhstNm',
        type: 'text',
        width: '200px',
        sortable: true,
      },
      {
        key: 'address',
        name: 'REPO.TABLE.mrhst.address',
        type: 'text',
      },
      {
        key: 'openFl',
        name: 'REPO.TABLE.mrhst.openFl',
        type: 'boolean',
        width: '80px',
        class: 'text-center',
      },
      {
        key: 'sort',
        name: 'REPO.TABLE.mrhst.sort',
        type: 'number',
        width: '80px',
        class: 'text-center',
        sortable: true,
      },
    ],
    goodsColumns: COMMON_GOODS_COLUMNS,
    hasOptions: true,
    goodsOptFieldName: 'farmGoodsOptList',
    goodsOptMasterFieldName: 'farmGoodsOptMasterList',
    excelTemplate: 'farm-mrhst',
    goodsExtraFields: [COST_TO_TEAM_FIELD],
    defaultPageSize: 10,
    defaultSort: 'sort,asc',
    routePath: 'master/farm-mrhst',
    permissions: ['FARM_GOODS$READ'],
  },
};
